import { ReactNode, forwardRef, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import clsx from 'clsx'

import { makeStyles, Box, Button, Tooltip, Theme } from '@material-ui/core'
import { CSSProperties } from '@material-ui/styles'

import { ReactComponent as IconPlayInExtension } from 'assets/icons/icon-play-in-ext.svg'

import { PlayControls } from '@guidde-co/shared.video-player.play-controls'

import { setDuration } from 'ducks/actions'

import { useBoolean, useWindowView } from 'hooks'
import { sendExtMessage, openLink, round } from 'modules'

type StyleProps = {
    roundBorders?: boolean
}

const useStyles = makeStyles<Theme, StyleProps>(theme => ({
    container: {
        position: 'relative',
        width: '100%',
        overflow: 'hidden'
    },
    video: {
        cursor: 'pointer',
        maxHeight: '60vh',
        display: 'block',
        background: 'black'
    },
    speedBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgb(38 50 56 / 50%)',
        minWidth: '40px',
        minHeight: '40px',
        borderRadius: '50%',
        padding: 0,
        color: '#fff',
        '&:hover': {
            backgroundColor: 'rgb(38 50 56 / 80%)'
        }
    },
    hoverControls: {
        position: 'absolute',
        top: '20px',
        right: '23px',
        '& > *': {
            marginBottom: theme.spacing(1),
            '&:nth-child(2)': {
                transitionDelay: '0.1s'
            },
            '&:nth-child(3)': {
                transitionDelay: '0.2s'
            },
            '&:nth-child(4)': {
                transitionDelay: '0.3s'
            },
            '&:nth-child(5)': {
                transitionDelay: '0.4s'
            }
        }
    },
    animated: {
        '& > *': {
            opacity: 0,
            transform: 'translateX(30px)',
            transition: 'opacity 0.1s linear, transform 0.1s linear'
        }
    },
    visible: {
        opacity: 1,
        zIndex: 1,
        '& > *': {
            opacity: 1,
            transform: 'translateX(0)',
            transition: 'opacity 0.1s linear, transform 0.1s linear'
        }
    },
    playInExtBtn: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    playControls: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        zIndex: 1
    },
    videoCorners: {
        borderTopLeftRadius: ({ roundBorders }) => (roundBorders ? '12px' : 0),
        borderTopRightRadius: ({ roundBorders }) => (roundBorders ? '12px' : 0),
        overflow: 'hidden'
    }
}))

type Props = {
    playbookUrl: string
    playbookId: string
    link: string
    isPlaylist?: boolean
    roundBorders?: boolean
    style?: CSSProperties
    showCaptions?: boolean
    renderPlayOnExt?: boolean
    hoverControls?: ReactNode
    subtitlesUrl?: string
    controls?: boolean
    onPlay?: () => void
    onPause?: () => void
    onEnded?: () => void
    onTimeUpdate?: (e: any) => void
}

type StatusType = 'loading' | 'playing' | 'stopped'

export const ControlledVideoPlayer = forwardRef<HTMLVideoElement | null, Props>(
    (
        {
            playbookUrl,
            subtitlesUrl,
            hoverControls,
            showCaptions,
            playbookId,
            link,
            isPlaylist,
            roundBorders = false,
            renderPlayOnExt,
            onPlay,
            onTimeUpdate,
            onPause,
            onEnded,
            ...videoProps
        },
        ref: any
    ) => {
        const classes = useStyles({ roundBorders })
        const dispatch = useDispatch()

        const hoverState = useBoolean()
        const { isMobileView } = useWindowView()
        const hidePlayControls = useBoolean()

        const showSubtitle = useBoolean(showCaptions)
        const setSubtitle = showSubtitle.set

        useEffect(() => {
            setSubtitle(!showCaptions)
        }, [setSubtitle, showCaptions])

        const [status, setStatus] = useState<StatusType>('loading')

        const isExtensionInstalled = useSelector(state => state.extensionStatus.isInstalled)

        const handlePlayInExtension = () => {
            sendExtMessage({
                type: 'play',
                playbookId,
                isPlaylist
            })
            openLink(link)
        }

        const showPlayControls = hidePlayControls.isFalse && !videoProps.controls && !isMobileView

        return (
            <Box
                className={classes.container}
                onMouseEnter={hoverState.setTrue}
                onMouseLeave={hoverState.setFalse}
            >
                <Box
                    zIndex={1}
                    position="relative"
                    className={classes.videoCorners}
                    onClick={() => {
                        if (!ref?.current || status === 'loading' || videoProps.controls) return

                        if (status === 'playing') {
                            ref.current.pause()
                        } else {
                            ref.current.play()
                        }
                    }}
                >
                    <video
                        crossOrigin="anonymous"
                        className={classes.video}
                        ref={ref}
                        width="100%"
                        height="auto"
                        preload="auto"
                        autoPlay={!isMobileView}
                        controlsList="nodownload"
                        controls={isMobileView}
                        onContextMenu={e => e.preventDefault()}
                        onLoadedData={() => {
                            hidePlayControls.setFalse()
                            setStatus('stopped')
                        }}
                        onSeeking={() => setStatus('loading')}
                        onSeeked={(e: any) => {
                            const status = e.target.paused ? 'stopped' : 'playing'
                            setStatus(status)
                        }}
                        onDurationChange={(e: any) => {
                            dispatch(setDuration(round(e.target.duration)))
                        }}
                        onTimeUpdate={e => onTimeUpdate?.(e)}
                        onPlay={() => {
                            onPlay?.()
                            setStatus('playing')
                        }}
                        onPause={() => {
                            onPause?.()
                            setStatus('stopped')
                        }}
                        onEnded={() => onEnded?.()}
                        {...videoProps}
                        src={playbookUrl}
                    >
                        {subtitlesUrl && showSubtitle.isTrue && (
                            <track
                                label="English"
                                kind="subtitles"
                                srcLang="en"
                                src={subtitlesUrl}
                                default
                            />
                        )}
                    </video>
                </Box>
                {showPlayControls && (
                    <Box className={classes.playControls} mt="-10px">
                        <PlayControls
                            onClick={() => {
                                if (status === 'playing') {
                                    ref.current.pause()
                                    return
                                }
                                ref.current.play()
                            }}
                            visible={hoverState.isTrue}
                            status={status}
                        />
                    </Box>
                )}
                <Box
                    display="flex"
                    flexDirection="column"
                    className={clsx(
                        classes.hoverControls,
                        classes.animated,
                        hoverState.isTrue && classes.visible
                    )}
                >
                    {isExtensionInstalled && link && renderPlayOnExt && (
                        <Tooltip title="Play on app">
                            <Button
                                className={clsx(classes.speedBtn, classes.playInExtBtn)}
                                color="default"
                                onClick={handlePlayInExtension}
                            >
                                <IconPlayInExtension style={{ height: '25px', width: '25px' }} />
                            </Button>
                        </Tooltip>
                    )}

                    {hoverControls}
                </Box>
            </Box>
        )
    }
)
